
import React, { useState } from 'react';
import { X, RefreshCw, Check, Copy, ShieldCheck, AlertTriangle, User, Mail, Shield, Send, Key } from 'lucide-react';
import { AppUser } from '../../types';
import { supabase } from '../../lib/supabase';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';

interface PasswordModalProps {
  user: AppUser;
  onClose: () => void;
}

export const PasswordModal: React.FC<PasswordModalProps> = ({ user, onClose }) => {
  const [stage, setStage] = useState<'options' | 'manual' | 'success_email' | 'success_manual'>('options');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generatePassword = () => {
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()";
    let retVal = "";
    for (let i = 0; i < 14; ++i) retVal += charset.charAt(Math.floor(Math.random() * charset.length));
    setNewPassword(retVal);
  };

  const handleEmailReset = async () => {
    setLoading(true);
    setError(null);
    try {
      // SMTP Reset: Uses working Supabase SMTP settings
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
      });
      if (resetError) throw resetError;
      setStage('success_email');
    } catch (err: any) {
      setError(err.message || "Failed to send reset email. Ensure user exists in Auth directory.");
    } finally {
      setLoading(false);
    }
  };

  const handleManualApply = async () => {
    if (!user.auth_user_id) {
      setError("No linked identity. User must sign in once or be invited before a manual override.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { error: rpcError } = await supabase.rpc('admin_reset_password', {
        p_user_id: user.auth_user_id,
        p_new_password: newPassword
      });
      if (rpcError) throw rpcError;
      setStage('success_manual');
    } catch (err: any) {
      setError(err.message || "Credential override failed.");
    } finally {
      setLoading(false);
    }
  };

  if (stage === 'success_email') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
        <div className="bg-white w-full max-w-md rounded-[2.5rem] p-10 text-center shadow-2xl animate-in zoom-in-95 duration-200">
          <div className="w-20 h-20 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={40} />
          </div>
          <h3 className="text-2xl font-black uppercase text-slate-900 mb-2">Email Sent</h3>
          <p className="text-slate-500 text-xs font-bold leading-relaxed uppercase tracking-widest mb-8">
            A secure recovery link has been dispatched to {user.email}.
          </p>
          <Button variant="primary" onClick={onClose} className="w-full py-4">Return to Directory</Button>
        </div>
      </div>
    );
  }

  if (stage === 'success_manual') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
        <div className="bg-white w-full max-w-lg rounded-[2.5rem] overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 border border-slate-100">
          <div className="p-8 border-b border-slate-100 flex items-center justify-between bg-slate-900 text-[#FFDB00]">
            <h3 className="text-xl font-black uppercase tracking-tight flex items-center gap-3"><ShieldCheck size={24} /> Manual Override Applied</h3>
            <button onClick={onClose}><X size={24} /></button>
          </div>
          <div className="p-10 space-y-6">
            <div className="bg-slate-50 p-8 rounded-[1.5rem] border border-slate-200 font-mono text-sm space-y-4">
               <div>
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1">Email Account</span>
                  <span className="text-slate-900 font-bold">{user.email}</span>
               </div>
               <div className="pt-4 border-t border-slate-100">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1">Temporary Key</span>
                  <span className="text-2xl text-slate-900 font-black tracking-tight">{newPassword}</span>
               </div>
            </div>
            <div className="flex gap-3">
              <Button className="flex-1 py-4" variant="primary" onClick={() => { navigator.clipboard.writeText(newPassword); setCopied(true); }} icon={copied ? <Check size={18} /> : <Copy size={18} />}>
                {copied ? 'Copied to Clipboard' : 'Copy Access Key'}
              </Button>
              <Button variant="ghost" onClick={onClose}>Done</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 border border-slate-100">
        <div className="p-10 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-2xl font-black uppercase tracking-tight text-slate-900">Credential Sync</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 transition-colors"><X size={28} /></button>
        </div>
        
        <div className="p-10 space-y-8">
          <div className="flex items-center gap-4 px-6 py-4 bg-slate-50 rounded-2xl border border-slate-100">
            <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-slate-400 shrink-0"><User size={24} /></div>
            <div className="overflow-hidden">
              <p className="text-sm font-black text-slate-900 uppercase truncate tracking-tight">{user.full_name || 'System Identity'}</p>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate">{user.email}</p>
            </div>
          </div>

          {error && <div className="p-4 bg-rose-50 border border-rose-100 rounded-xl text-[11px] font-bold text-rose-600 uppercase tracking-wide leading-relaxed text-center">{error}</div>}

          {stage === 'options' ? (
            <div className="grid grid-cols-1 gap-4">
              <Button variant="primary" onClick={handleEmailReset} isLoading={loading} className="py-5" icon={<Send size={18} />}>
                Send Password Reset Email
              </Button>
              <Button variant="outline" onClick={() => setStage('manual')} className="py-5" icon={<Key size={18} />}>
                Manual Key Override
              </Button>
              <p className="text-center text-[9px] font-black text-slate-300 uppercase tracking-[0.2em] pt-4 italic">Email reset uses verified SMTP handshake</p>
            </div>
          ) : (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              <div className="relative group">
                <Input label="Direct Access Password" placeholder="Min 12 characters" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="pr-12 py-4 shadow-inner text-lg tracking-widest font-mono" type="text" />
                <button onClick={generatePassword} type="button" className="absolute right-4 top-10 text-slate-300 hover:text-slate-900 transition-colors"><RefreshCw size={22} /></button>
              </div>
              <div className="flex gap-4">
                <Button variant="ghost" onClick={() => setStage('options')} className="px-8">Back</Button>
                <Button variant="primary" onClick={handleManualApply} isLoading={loading} disabled={!newPassword || newPassword.length < 8} className="flex-1 py-4">Apply Manual Key</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
